import {useEffect, useRef, useState, type ReactNode} from 'react'
import {hasWebGLSupport} from '../../../../../../../lib/utils/platform'
import Artwork from './webgl/artwork'
import Styles from './WebGLFooter.module.css'

type Props = {
  fallback?: ReactNode
  onFallback?: () => void
}

const STATIC_RENDERING_MEDIA_QUERY = '(max-width: 1023px)'

export function WebGLFooter({fallback, onFallback}: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rAFRef = useRef<number | null>(null)
  const artworkRef = useRef<Artwork | null>(null)
  const [hasWebGL, setHasWebGL] = useState(true)

  useEffect(() => {
    if (!hasWebGL) {
      return
    }

    if (!hasWebGLSupport()) {
      const fallbackTimeout = window.setTimeout(() => {
        onFallback?.()
        setHasWebGL(false)
      }, 0)

      return () => window.clearTimeout(fallbackTimeout)
    }

    if (!wrapperRef.current || !canvasRef.current) return

    let isInViewport = false
    let isVisible = false
    let isInit = false
    let hasFailed = false
    let hasCompletedOpening = false
    let fallbackTimeout: number | undefined
    const staticRenderingMediaQuery = window.matchMedia(STATIC_RENDERING_MEDIA_QUERY)
    let usesStaticRendering = staticRenderingMediaQuery.matches

    const stopRendering = () => {
      if (rAFRef.current === null) return

      cancelAnimationFrame(rAFRef.current)
      rAFRef.current = null
    }

    const handleRaf = () => {
      if (!isInViewport || !isVisible) {
        rAFRef.current = null
        return
      }

      artworkRef.current?.update(isInViewport, isVisible)

      if (usesStaticRendering && hasCompletedOpening) {
        rAFRef.current = null
        return
      }

      rAFRef.current = requestAnimationFrame(handleRaf)
    }

    const startRendering = () => {
      if (rAFRef.current !== null || !isInViewport || !isVisible || (usesStaticRendering && hasCompletedOpening)) {
        return
      }

      rAFRef.current = requestAnimationFrame(handleRaf)
    }

    const handleOpeningComplete = () => {
      hasCompletedOpening = true
      artworkRef.current?.update(true, true)

      if (usesStaticRendering) {
        stopRendering()
      }
    }

    const handleArtworkError = (error: unknown) => {
      if (hasFailed) return

      hasFailed = true
      window.reportError(error)
      stopRendering()
      fallbackTimeout = window.setTimeout(() => {
        onFallback?.()
        setHasWebGL(false)
      }, 0)
    }

    const handleResize = ({height, width}: Pick<DOMRect, 'height' | 'width'>) => {
      if (!canvasRef.current || hasFailed) return

      if (!isInit) {
        try {
          artworkRef.current = new Artwork({
            canvas: canvasRef.current,
            pixelRatio: Math.min(window.devicePixelRatio, 1.5),
            reduceMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
            onError: handleArtworkError,
            onOpeningComplete: handleOpeningComplete,
          })
          isInit = true
        } catch (error) {
          handleArtworkError(error)
          return
        }
      }

      artworkRef.current?.resize(width, height)
      isVisible = width > 0 && height > 0

      if (isVisible && (!usesStaticRendering || !hasCompletedOpening)) {
        startRendering()
      } else {
        stopRendering()
      }
    }

    const handleMouseMove = (event: MouseEvent) => {
      if (!isInViewport || !isVisible || (usesStaticRendering && hasCompletedOpening)) return

      artworkRef.current?.handleMouseMove(event)
    }

    const handleStaticRenderingChange = (event: MediaQueryListEvent) => {
      usesStaticRendering = event.matches

      if (usesStaticRendering && hasCompletedOpening) {
        artworkRef.current?.update(true, true)
        stopRendering()
      } else {
        startRendering()
      }
    }

    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        isInViewport = entry.isIntersecting

        if (isInViewport) {
          artworkRef.current?.startOpening()
          startRendering()
        } else {
          stopRendering()
        }
      }
    })

    const wrapper = wrapperRef.current
    handleResize(wrapper.getBoundingClientRect())
    observer.observe(wrapper)

    const resizeObserver = new ResizeObserver(([entry]) => {
      if (entry) handleResize(entry.contentRect)
    })
    resizeObserver.observe(wrapper)
    staticRenderingMediaQuery.addEventListener('change', handleStaticRenderingChange)
    window.addEventListener('mousemove', handleMouseMove)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      staticRenderingMediaQuery.removeEventListener('change', handleStaticRenderingChange)
      observer.disconnect()
      resizeObserver.disconnect()
      stopRendering()
      if (fallbackTimeout !== undefined) {
        window.clearTimeout(fallbackTimeout)
      }
      artworkRef.current?.dispose()
      artworkRef.current = null
    }
  }, [hasWebGL, onFallback])

  return (
    <div ref={wrapperRef} className={Styles.Wrapper}>
      {hasWebGL && <canvas ref={canvasRef} className={Styles.Canvas} />}
      {!hasWebGL && (fallback ?? <p>Your browser does not support WebGL.</p>)}
    </div>
  )
}
